import React from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Translate as TranslateIcon,
  Science as ScienceIcon,
  Apps as AppsIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  Palette as PaletteIcon,
  Image as SplashIcon,
  TrendingUp as TrendingUpIcon,
  CameraAlt as CameraIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  ExitToApp as LogoutIcon,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  BarChart as AppGrowthIcon,
  Settings as SettingsIcon,
  Campaign as CampaignIcon,
  SmartDisplay as UGCVideoIcon,
  Slideshow as SlideshowMakerIcon,
  Pets as MascotCreatorIcon,
  Store as StoreIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  RocketLaunch as PublishReadyIcon,
  CropLandscape as FeatureGraphicIcon,
  FactCheck as ViolationCheckerIcon,
  Reviews as ReviewsIcon,
  AccountTree as ContextHubIcon,
  QueryStats as AnalyticsPlannerIcon,
  Science as ExperimentWorkbenchIcon,
  CompareArrows as CompetitorBuilderIcon,
  Link as SmartLinkIcon,
  CalendarMonth as ContentCalendarIcon,
  Share as SocialRepurposerIcon,
  AlternateEmail as OutreachAssistantIcon,
  AttachMoney as PricingStrategistIcon,
  Lock as PaywallOptimizerIcon,
  Login as OnboardingOptimizerIcon,
  Shield as ChurnPreventionIcon,
  Subscriptions as SubscriptionsIcon,
  GroupAdd as ReferralBuilderIcon,
  TaskAlt as ReadinessScoreIcon,
  NotificationsActive as FCMAutomationIcon,
  Tune as ScreenshotOptimizerIcon,
  Insights as WeeklyGrowthOperatorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 88;

interface SideNavProps {
  collapsed: boolean;
  autoHideEnabled: boolean;
  onToggleCollapse: () => void;
  onHoverExpandChange?: (expanded: boolean) => void;
}

const SideNav: React.FC<SideNavProps> = ({
  collapsed: navigationCollapsed,
  autoHideEnabled,
  onToggleCollapse,
  onHoverExpandChange,
}) => {
  const { user, logout, clearToken, githubConnected } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [toolSearch, setToolSearch] = React.useState("");
  const [searchExpanded, setSearchExpanded] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const toolListRef = React.useRef<HTMLUListElement>(null);
  const collapsed = navigationCollapsed && !searchExpanded;
  const searchTerms = toolSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const isSearching = searchTerms.length > 0;

  React.useEffect(() => {
    setToolSearch("");
    setSearchExpanded(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  React.useEffect(() => {
    if (toolListRef.current) toolListRef.current.scrollTop = 0;
  }, [toolSearch]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [sectionOpen, setSectionOpen] = React.useState<Record<string, boolean>>(
    {
      Foundation: true,
      "Research & Strategy": true,
      "Creative Production": true,
      Monetization: true,
      "Growth & Optimization": true,
      Advanced: true,
      "Build Execution": true,
    },
  );

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const handleClearTokenRequest = () => {
    handleClose();
    setConfirmDialogOpen(true);
  };

  const handleClearTokenConfirm = () => {
    setConfirmDialogOpen(false);
    clearToken();
  };

  const toggleSection = (title: string) => {
    setSectionOpen((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const navigationSections = [
    {
      title: "Foundation",
      description: "Set up the app, compliance, and release readiness.",
      items: [
        {
          label: "Dashboard",
          icon: <DashboardIcon />,
          path: "/",
        },
        {
          label: "App Registration",
          icon: <AppsIcon />,
          path: "/app-registration",
        },
        {
          label: "Legal",
          icon: <GavelIcon />,
          path: "/legal",
        },
        {
          label: "Deployment Checklist",
          icon: <AssignmentIcon />,
          path: "/deployment-checklist",
        },
        {
          label: "Publish Ready Dashboard",
          icon: <PublishReadyIcon />,
          path: "/publish-ready",
        },
        {
          label: "Translation",
          icon: <TranslateIcon />,
          path: "/translation",
        },
        {
          label: "Settings",
          icon: <SettingsIcon />,
          path: "/settings",
        },
      ],
    },
    {
      title: "Research & Strategy",
      description: "Research opportunities and define product direction.",
      items: [
        {
          label: "New App Idea Research",
          icon: <TrendingUpIcon />,
          path: "/app-idea-research",
        },
        {
          label: "Existing App Research",
          icon: <ScienceIcon />,
          path: "/app-research",
        },
        {
          label: "Review Analysis",
          icon: <ReviewsIcon />,
          path: "/review-analysis",
        },
        {
          label: "Implementation Plan Generator",
          icon: <SettingsIcon />,
          path: "/implementation-plan-generator",
        },
      ],
    },
    {
      title: "Creative Production",
      description: "Generate visual assets and store-ready creative.",
      items: [
        {
          label: "Icon Generator",
          icon: <PaletteIcon />,
          path: "/icon-generator",
        },
        {
          label: "Splash Screen Creator",
          icon: <SplashIcon />,
          path: "/splash-screen-generator",
        },
        {
          label: "Feature Graphic",
          icon: <FeatureGraphicIcon />,
          path: "/feature-graphic-generator",
        },
        {
          label: "Apple Creative Assets",
          icon: <FeatureGraphicIcon />,
          path: "/apple-creative-assets",
        },
        {
          label: "Screenshot Creator",
          icon: <CameraIcon />,
          path: "/screenshot-creator",
        },
        {
          label: "UGC Video Creator",
          icon: <UGCVideoIcon />,
          path: "/ugc-video-creator",
        },
        {
          label: "Slide Show Maker",
          icon: <SlideshowMakerIcon />,
          path: "/slideshow-maker",
        },
        {
          label: "Mascot Creator",
          icon: <MascotCreatorIcon />,
          path: "/mascot-creator",
        },
        {
          label: "App Store Listing",
          icon: <StoreIcon />,
          path: "/app-store-listing",
        },
        {
          label: "Google Play Release Notes",
          icon: <DescriptionIcon />,
          path: "/google-play-release-notes",
        },
      ],
    },
    {
      title: "Monetization",
      description: "Design monetization infrastructure and recurring revenue.",
      items: [
        {
          label: "Subscriptions",
          icon: <SubscriptionsIcon />,
          path: "/subscriptions",
        },
      ],
    },
    {
      title: "Growth & Optimization",
      description: "Scale distribution, engagement, and discoverability.",
      items: [
        {
          label: "App Marketing",
          icon: <CampaignIcon />,
          path: "/app-marketing",
        },
        {
          label: "Programmatic SEO",
          icon: <SearchIcon />,
          path: "/programmatic-seo",
        },
        {
          label: "Cross Promotion",
          icon: <CampaignIcon />,
          path: "/cross-promotion",
        },
        {
          label: "Smart Links",
          icon: <SmartLinkIcon />,
          path: "/smart-links",
        },
        {
          label: "App Engagement",
          icon: <NotificationsIcon />,
          path: "/app-engagement",
        },
        {
          label: "App Growth",
          icon: <AppGrowthIcon />,
          path: "/app-growth",
        },
        {
          label: "App Performance",
          icon: <AssessmentIcon />,
          path: "/app-performance",
        },
        {
          label: "Violation Checker",
          icon: <ViolationCheckerIcon />,
          path: "/app-store-violation-checker",
        },
      ],
    },
    {
      title: "Build Execution",
      description: "Engineer and ship the product implementation.",
      items: [
        {
          label: "Development Framework",
          icon: <CodeIcon />,
          path: "/development-framework",
        },
      ],
    },
    {
      title: "Advanced",
      description: "Advanced AI growth operators and optimization engines.",
      items: [
        {
          label: "Product Context Hub",
          icon: <ContextHubIcon />,
          path: "/advanced/context-hub",
        },
        {
          label: "Analytics Planner",
          icon: <AnalyticsPlannerIcon />,
          path: "/advanced/analytics-tracking-planner",
        },
        {
          label: "Experiment Workbench",
          icon: <ExperimentWorkbenchIcon />,
          path: "/advanced/experiment-workbench",
        },
        {
          label: "Marketing Optimizer",
          icon: <CampaignIcon />,
          path: "/advanced/marketing-optimizer",
        },
        {
          label: "pSEO Publisher",
          icon: <SearchIcon />,
          path: "/advanced/pseo-publisher",
        },
        {
          label: "Competitor Builder",
          icon: <CompetitorBuilderIcon />,
          path: "/advanced/competitor-page-builder",
        },
        {
          label: "Content Calendar",
          icon: <ContentCalendarIcon />,
          path: "/advanced/content-calendar",
        },
        {
          label: "Social Repurposer",
          icon: <SocialRepurposerIcon />,
          path: "/advanced/social-repurposer",
        },
        {
          label: "Outreach Assistant",
          icon: <OutreachAssistantIcon />,
          path: "/advanced/outreach-assistant",
        },
        {
          label: "Pricing Strategist",
          icon: <PricingStrategistIcon />,
          path: "/advanced/pricing-strategist",
        },
        {
          label: "Paywall Optimizer",
          icon: <PaywallOptimizerIcon />,
          path: "/advanced/paywall-optimizer",
        },
        {
          label: "Onboarding Optimizer",
          icon: <OnboardingOptimizerIcon />,
          path: "/advanced/onboarding-optimizer",
        },
        {
          label: "Churn Prevention",
          icon: <ChurnPreventionIcon />,
          path: "/advanced/churn-prevention",
        },
        {
          label: "Referral Builder",
          icon: <ReferralBuilderIcon />,
          path: "/advanced/referral-builder",
        },
        {
          label: "Copy Quality Gate",
          icon: <ViolationCheckerIcon />,
          path: "/advanced/copy-quality-gate",
        },
        {
          label: "Readiness Score",
          icon: <ReadinessScoreIcon />,
          path: "/advanced/readiness-quality-score",
        },
        {
          label: "FCM Automation",
          icon: <FCMAutomationIcon />,
          path: "/advanced/fcm-lifecycle-automation",
        },
        {
          label: "Legal Strategist",
          icon: <GavelIcon />,
          path: "/advanced/legal-jurisdiction-generator",
        },
        {
          label: "Screenshot Optimizer",
          icon: <ScreenshotOptimizerIcon />,
          path: "/advanced/screenshot-pipeline-optimizer",
        },
        {
          label: "Weekly Growth Operator",
          icon: <WeeklyGrowthOperatorIcon />,
          path: "/advanced/weekly-growth-operator",
        },
      ],
    },
  ];

  const visibleSections = navigationSections.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      const searchableText = `${item.label} ${item.path.replace(/[-/]/g, " ")} ${section.title}`.toLowerCase();
      return searchTerms.every((term) => searchableText.includes(term));
    }),
  })).filter((section) => section.items.length > 0);
  const matchCount = visibleSections.reduce((total, section) => total + section.items.length, 0);

  const resetSearch = () => {
    setToolSearch("");
    searchInputRef.current?.focus();
  };

  return (
    <>
      <Drawer
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setSearchExpanded(false);
          }
        }}
        onMouseEnter={() => onHoverExpandChange?.(true)}
        onMouseLeave={() => onHoverExpandChange?.(false)}
        sx={{
          width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
          flexShrink: 0,
          transition: "width 260ms cubic-bezier(0.22, 1, 0.36, 1)",
          "& .MuiDrawer-paper": {
            width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
            boxSizing: "border-box",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(180deg, #102133 0%, #0f1f2f 24%, #10262c 55%, #111e2d 100%)"
                : "linear-gradient(180deg, #e8f2ff 0%, #f1f8ff 24%, #f4fbf9 55%, #f8fbff 100%)",
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            transition:
              "width 260ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms ease, transform 260ms ease",
            overflowX: "hidden",
            overflowY: "hidden",
            boxShadow: collapsed
              ? "0 10px 28px rgba(15,23,42,0.08)"
              : "0 18px 40px rgba(15,23,42,0.12)",
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            flexShrink: 0,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.26 : 0.18,
              )} 0%, ${alpha(
                theme.palette.success.main,
                theme.palette.mode === "dark" ? 0.2 : 0.12,
              )} 100%)`,
            borderBottom: (theme) =>
              `1px solid ${alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.32 : 0.18,
              )}`,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              justifyContent: collapsed ? "center" : "flex-start",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <AppsIcon fontSize="small" />
            </Avatar>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.primary",
                    fontWeight: "bold",
                    fontSize: "1.32rem",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                    display: "block",
                    opacity: collapsed ? 0 : 1,
                    transform: collapsed ? "translateX(-8px)" : "translateX(0)",
                    transition:
                      "opacity 180ms ease 40ms, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                >
                  App Success Platform
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    display: "block",
                    mt: 0.35,
                    lineHeight: 1.2,
                    opacity: collapsed ? 0 : 1,
                    transform: collapsed ? "translateX(-8px)" : "translateX(0)",
                    transition:
                      "opacity 180ms ease 70ms, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                >
                  Build, launch, and scale winning apps
                </Typography>
              </Box>
            )}
          </Box>
          <Tooltip
            title={
              autoHideEnabled
                ? "Auto-hide navigation is managed in Settings"
                : collapsed
                  ? "Expand navigation"
                  : "Collapse navigation"
            }
          >
            <span>
              <IconButton
                onClick={() => {
                  setToolSearch("");
                  setSearchExpanded(false);
                  if (!navigationCollapsed || !searchExpanded) onToggleCollapse();
                }}
                disabled={autoHideEnabled}
                size="small"
                sx={{
                  ml: collapsed ? 0 : "auto",
                  mt: collapsed ? 1.25 : 0,
                  bgcolor: (theme) =>
                    alpha(
                      theme.palette.background.paper,
                      theme.palette.mode === "dark" ? 0.3 : 0.72,
                    ),
                  border: (theme) =>
                    `1px solid ${alpha(
                      theme.palette.primary.main,
                      theme.palette.mode === "dark" ? 0.28 : 0.18,
                    )}`,
                  alignSelf: collapsed ? "center" : "flex-start",
                  "&:hover": {
                    bgcolor: (theme) =>
                      alpha(
                        theme.palette.background.paper,
                        theme.palette.mode === "dark" ? 0.44 : 0.92,
                      ),
                  },
                }}
              >
                {collapsed ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ChevronLeftIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ px: collapsed ? 2 : 1.5, py: 1.5, flexShrink: 0 }}>
          {collapsed ? (
            <Tooltip title="Search tools" placement="right">
              <IconButton aria-label="Search tools" onClick={() => setSearchExpanded(true)}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <TextField
              fullWidth
              size="small"
              placeholder="Search tools…"
              inputRef={searchInputRef}
              value={toolSearch}
              onFocus={() => setSearchExpanded(true)}
              onChange={(event) => setToolSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  setToolSearch("");
                  setSearchExpanded(false);
                } else if (event.key === "Enter" && isSearching && matchCount > 0) {
                  event.preventDefault();
                  navigate(visibleSections[0].items[0].path);
                  setToolSearch("");
                  setSearchExpanded(false);
                }
              }}
              inputProps={{ "aria-label": "Search tools", "aria-controls": "sidebar-tools", autoComplete: "off" }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: toolSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear tool search" onMouseDown={(event) => event.preventDefault()} onClick={resetSearch}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          )}
          {!collapsed && isSearching && (
            <Typography role="status" aria-live="polite" variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {matchCount === 0 ? "No tools found. Try another name." : `${matchCount} ${matchCount === 1 ? "tool" : "tools"} found`}
            </Typography>
          )}
        </Box>

        <List ref={toolListRef} id="sidebar-tools" aria-label="Tools" sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1 }}>
          {visibleSections.map((section) => (
            <Box
              key={section.title}
              sx={{
                mb: 1.25,
                border: (theme) =>
                  `1px solid ${alpha(
                    theme.palette.primary.main,
                    collapsed
                      ? theme.palette.mode === "dark"
                        ? 0.24
                        : 0.14
                      : theme.palette.mode === "dark"
                      ? 0.34
                      : 0.22,
                  )}`,
                borderRadius: 2,
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === "dark" ? 0.56 : 0.72,
                  ),
                backdropFilter: "blur(4px)",
                overflow: "hidden",
                boxShadow: (theme) =>
                  `0 3px 12px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.26 : 0.08,
                  )}`,
              }}
            >
              {!collapsed && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => { if (!isSearching) toggleSection(section.title); }}
                    aria-expanded={isSearching || !!sectionOpen[section.title]}
                    sx={{
                      py: 1,
                      px: 1.25,
                      bgcolor: (theme) =>
                        alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === "dark" ? 0.2 : 0.08,
                        ),
                      borderBottom: (theme) =>
                        (isSearching || sectionOpen[section.title])
                          ? `1px solid ${alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === "dark" ? 0.34 : 0.2,
                            )}`
                          : "none",
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.28 : 0.12,
                          ),
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          color: "text.primary",
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          lineHeight: 1.3,
                          display: "block",
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.2,
                          display: "block",
                        }}
                      >
                        {section.description}
                      </Typography>
                    </Box>
                    {isSearching || sectionOpen[section.title] ? (
                      <ExpandLessIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              )}
              <Collapse
                in={collapsed || isSearching || sectionOpen[section.title]}
                timeout="auto"
                unmountOnExit={!collapsed}
              >
                {section.items.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <Tooltip
                      title={collapsed ? `${section.title}: ${item.label}` : ""}
                      placement="right"
                    >
                      <ListItemButton
                        component={RouterLink}
                        to={item.path}
                        aria-label={item.label}
                        onClick={() => { setToolSearch(""); setSearchExpanded(false); }}
                        selected={isActive(item.path)}
                        sx={{
                          py: 0.85,
                          px: collapsed ? 1 : 1.25,
                          minHeight: collapsed ? 48 : "auto",
                          justifyContent: collapsed ? "center" : "flex-start",
                          borderRadius: 0,
                          "&.Mui-selected": (theme) => ({
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === "dark" ? 0.28 : 0.18,
                            ),
                            borderRight: collapsed
                              ? "none"
                              : `4px solid ${theme.palette.primary.main}`,
                            boxShadow: collapsed
                              ? `inset 3px 0 0 ${theme.palette.primary.main}`
                              : "none",
                            "& .MuiListItemIcon-root": {
                              color: "primary.main",
                            },
                            "& .MuiListItemText-primary": {
                              color: "primary.main",
                              fontWeight: 700,
                            },
                          }),
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === "dark" ? 0.16 : 0.1,
                              ),
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: collapsed ? 0 : 36,
                            color: "inherit",
                            justifyContent: "center",
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: "0.92rem" }}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </Collapse>
            </Box>
          ))}
        </List>

        <Divider />

        {user && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: collapsed ? 0 : 2,
                cursor: "pointer",
                justifyContent: collapsed ? "center" : "flex-start",
              }}
              onClick={handleMenu}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: "primary.main",
                  fontSize: "0.9rem",
                }}
              >
                {user.email?.charAt(0).toUpperCase()}
              </Avatar>
              {!collapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.name || user.email}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {user.email}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem
            onClick={handleClearTokenRequest}
            disabled={!githubConnected}
            sx={{ fontSize: "0.9rem" }}
          >
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            Clear GitHub Token
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ fontSize: "0.9rem" }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Drawer>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Clear GitHub Token</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear your GitHub authentication token?
            Translation repository features will require reconnecting it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
          <button
            onClick={handleClearTokenConfirm}
            style={{ color: "#d32f2f", fontWeight: "bold" }}
          >
            Clear Token
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { DRAWER_WIDTH };
export default SideNav;
